import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Plus, Trash2, Save, X } from 'lucide-react';
import axios from 'axios';
import { useToast } from '../../components/Toast';

const Overlay = styled.div`
  position: fixed; top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.8); z-index: 1000;
  display: flex; align-items: center; justify-content: center;
`;

const ModalContent = styled.div`
  background: #222; width: 800px; max-height: 90vh; border-radius: 8px;
  display: flex; flex-direction: column; overflow: hidden;
`;

const Header = styled.div`
  padding: 16px 24px; border-bottom: 1px solid #333;
  display: flex; justify-content: space-between; align-items: center;
  color: #fff; font-size: 18px; font-weight: bold;
`;

const Body = styled.div`
  padding: 24px; overflow-y: auto; flex: 1; display: flex; flex-direction: column; gap: 24px;
`;

const Footer = styled.div`
  padding: 16px 24px; border-top: 1px solid #333;
  display: flex; justify-content: flex-end; gap: 12px;
`;

const Section = styled.div`
  background: #2a2a2a; border-radius: 6px; padding: 16px; border: 1px solid #444;
`;

const SectionTitle = styled.h4`
  margin: 0 0 16px 0; color: #0072ff; display: flex; align-items: center; gap: 8px; font-size: 14px;
`;

const FormGroup = styled.div`
  display: flex; flex-direction: column; gap: 8px; margin-bottom: 12px;
`;

const Label = styled.label`
  font-size: 12px; color: #aaa; font-weight: bold;
`;

const Input = styled.input`
  background: #333; border: 1px solid #444; color: #fff; padding: 8px; border-radius: 4px;
  &:focus { outline: none; border-color: #0072ff; }
`;

const Select = styled.select`
  background: #333; border: 1px solid #444; color: #fff; padding: 8px; border-radius: 4px;
  &:focus { outline: none; border-color: #0072ff; }
`;

const Row = styled.div`
  display: flex; gap: 12px; align-items: center; margin-bottom: 8px;
`;

const Button = styled.button`
  background: ${props => props.primary ? '#0072ff' : '#444'};
  color: #fff; border: none; padding: 8px 16px; border-radius: 4px; cursor: pointer;
  display: flex; align-items: center; gap: 6px; font-size: 13px;
  &:hover { opacity: 0.9; }
`;

const IconButton = styled.button`
  background: transparent; border: none; color: #ff4ea1; cursor: pointer; display: flex; align-items: center;
  &:hover { opacity: 0.8; }
`;

export default function StrategyBuilderModal({ onClose, customStrategies, onSaveComplete }) {
    const { showToast } = useToast();

    const [selectedStrategyId, setSelectedStrategyId] = useState('new');

    const [strategy, setStrategy] = useState({
        id: `strat_${Date.now()}`,
        name: '',
        description: '',
        indicators: [],
        buyConditions: [],
        sellConditions: []
    });

    const handleSelectStrategy = (e) => {
        const val = e.target.value;
        setSelectedStrategyId(val);
        if (val === 'new') {
            setStrategy({
                id: `strat_${Date.now()}`,
                name: '',
                description: '',
                indicators: [],
                buyConditions: [],
                sellConditions: []
            });
        } else {
            const found = customStrategies.find(s => s.id === val);
            if (found) {
                // clone
                setStrategy(JSON.parse(JSON.stringify(found)));
            }
        }
    };

    const handleSave = async () => {
        if (!strategy.id || !strategy.name) {
            showToast('전략 ID와 이름은 필수입니다.', 'error');
            return;
        }
        try {
            const baseUrl = window.location.hostname === 'localhost' ? 'http://localhost:5000' : window.location.origin;
            await axios.post(`${baseUrl}/api/stock/backtest/strategies`, strategy);
            showToast('전략이 저장되었습니다.', 'success');
            onSaveComplete();
            onClose();
        } catch (err) {
            showToast('저장 실패', 'error');
            console.error(err);
        }
    };

    const handleDelete = async () => {
        if (selectedStrategyId === 'new') return;
        if (!window.confirm('정말 삭제하시겠습니까?')) return;

        try {
            const baseUrl = window.location.hostname === 'localhost' ? 'http://localhost:5000' : window.location.origin;
            await axios.delete(`${baseUrl}/api/stock/backtest/strategies/${strategy.id}`);
            showToast('삭제되었습니다.', 'success');
            onSaveComplete();
            onClose();
        } catch (err) {
            showToast('삭제 실패', 'error');
            console.error(err);
        }
    };

    const handleClone = () => {
        const newId = `strat_${Date.now()}`;
        setStrategy({
            ...strategy,
            id: newId,
            name: `${strategy.name} (복사본)`
        });
        setSelectedStrategyId('new');
        showToast('전략이 복사되었습니다. 새 이름으로 저장해주세요.', 'success');
    };

    const addIndicator = () => {
        setStrategy({
            ...strategy,
            indicators: [...strategy.indicators, { id: `ind_${Date.now()}`, type: 'SMA', period: 20 }]
        });
    };

    const updateIndicator = (index, field, value) => {
        const newInds = [...strategy.indicators];
        newInds[index][field] = value;
        setStrategy({ ...strategy, indicators: newInds });
    };

    const removeIndicator = (index) => {
        const newInds = [...strategy.indicators];
        newInds.splice(index, 1);
        setStrategy({ ...strategy, indicators: newInds });
    };

    const addCondition = (type) => {
        const list = strategy[type];
        if (list.length > 0) {
            list.push({ logic: 'AND' });
        }
        list.push({ lhs: 'close', operator: '>', rhs: '0' });
        setStrategy({ ...strategy, [type]: list });
    };

    const updateCondition = (type, index, field, value) => {
        const list = [...strategy[type]];
        list[index][field] = value;
        setStrategy({ ...strategy, [type]: list });
    };

    const removeCondition = (type, index) => {
        const list = [...strategy[type]];
        // also remove logic block if removing a condition
        if (index > 0 && list[index - 1].logic) {
            list.splice(index - 1, 2);
        } else if (index === 0 && list.length > 1 && list[1].logic) {
            list.splice(0, 2);
        } else {
            list.splice(index, 1);
        }
        setStrategy({ ...strategy, [type]: list });
    };

    const getIndicatorOptions = () => {
        let opts = [];
        strategy.indicators.forEach(ind => {
            if (ind.type === 'MACD') {
                opts.push({ value: `${ind.id}_macd`, label: `MACD Line` });
                opts.push({ value: `${ind.id}_macd_prev`, label: `MACD Line (이전)` });
                opts.push({ value: `${ind.id}_signal`, label: `MACD Signal` });
                opts.push({ value: `${ind.id}_signal_prev`, label: `MACD Signal (이전)` });
                opts.push({ value: `${ind.id}_histogram`, label: `MACD Hist` });
            } else if (ind.type === 'TBM') {
                opts.push({ value: `${ind.id}_trend`, label: `TBM Trend` });
                opts.push({ value: `${ind.id}_trend_prev`, label: `TBM Trend (이전)` });
            } else {
                opts.push({ value: ind.id, label: `${ind.type} (${ind.period})` });
                opts.push({ value: `${ind.id}_prev`, label: `${ind.type} (${ind.period}) (이전)` });
            }
        });
        return opts;
    };

    const ohlcOptions = [
        { value: 'close', label: '종가 (Close)' },
        { value: 'close_prev', label: '전일 종가 (Close Prev)' },
        { value: 'open', label: '시가 (Open)' },
        { value: 'open_prev', label: '전일 시가 (Open Prev)' },
        { value: 'high', label: '고가 (High)' },
        { value: 'high_prev', label: '전일 고가 (High Prev)' },
        { value: 'low', label: '저가 (Low)' },
        { value: 'low_prev', label: '전일 저가 (Low Prev)' },
        { value: 'volume', label: '거래량 (Volume)' },
        { value: 'volume_prev', label: '전일 거래량 (Volume Prev)' },
    ];

    return (
        <Overlay>
            <ModalContent>
                <Header>
                    <div>동적 전략 빌더</div>
                    <X style={{ cursor: 'pointer' }} onClick={onClose} />
                </Header>
                <Body>
                    <FormGroup>
                        <Label>편집할 전략 선택</Label>
                        <Select value={selectedStrategyId} onChange={handleSelectStrategy}>
                            <option value="new">+ 새 전략 만들기</option>
                            {customStrategies.map(s => (
                                <option key={s.id} value={s.id}>{s.name} ({s.id})</option>
                            ))}
                        </Select>
                    </FormGroup>

                    <Row>
                        <FormGroup style={{ flex: 1 }}>
                            <Label>전략 ID (영문/숫자)</Label>
                            <Input value={strategy.id} onChange={e => setStrategy({ ...strategy, id: e.target.value })} disabled={selectedStrategyId !== 'new'} />
                        </FormGroup>
                        <FormGroup style={{ flex: 2 }}>
                            <Label>전략 이름</Label>
                            <Input value={strategy.name} onChange={e => setStrategy({ ...strategy, name: e.target.value })} placeholder="예: 단기 골든크로스" />
                        </FormGroup>
                    </Row>

                    <Section>
                        <SectionTitle>
                            보조지표 정의 (Indicators)
                            <Button style={{ marginLeft: 'auto', padding: '4px 8px' }} onClick={addIndicator}><Plus size={14} /> 추가</Button>
                        </SectionTitle>
                        {strategy.indicators.length === 0 && <div style={{ color: '#888', fontSize: '13px' }}>추가된 지표가 없습니다.</div>}
                        {strategy.indicators.map((ind, idx) => (
                            <Row key={idx} style={{ background: '#333', padding: '8px', borderRadius: '4px' }}>
                                <Input title="지표 식별자" placeholder="ID" value={ind.id} onChange={e => updateIndicator(idx, 'id', e.target.value)} style={{ width: '100px' }} />
                                <Select value={ind.type} onChange={e => updateIndicator(idx, 'type', e.target.value)} style={{ width: '120px' }}>
                                    <option value="SMA">SMA (단순이평)</option>
                                    <option value="EMA">EMA (지수이평)</option>
                                    <option value="RSI">RSI</option>
                                    <option value="MACD">MACD</option>
                                    <option value="VOL_SMA">거래량 이평</option>
                                    <option value="TBM">TBM SuperTrend</option>
                                    <option value="ADX">ADX 추세강도</option>
                                    <option value="DISPARITY">이격도 (주가/이평선)</option>
                                    <option value="VWAP">VWAP (거래량가중평균가)</option>
                                </Select>
                                <Input title="기간 (Period)" type="number" placeholder="Period" value={ind.period || ''} onChange={e => updateIndicator(idx, 'period', Number(e.target.value))} style={{ width: '80px' }} />
                                <IconButton onClick={() => removeIndicator(idx)}><Trash2 size={16} /></IconButton>
                            </Row>
                        ))}
                    </Section>

                    <Section>
                        <SectionTitle>
                            매수 조건 (Buy Conditions)
                            <Button style={{ marginLeft: 'auto', padding: '4px 8px' }} onClick={() => addCondition('buyConditions')}><Plus size={14} /> 추가</Button>
                        </SectionTitle>
                        {strategy.buyConditions.length === 0 && <div style={{ color: '#888', fontSize: '13px' }}>조건이 없습니다. (항상 매수하지 않음)</div>}
                        {strategy.buyConditions.map((cond, idx) => (
                            <Row key={idx} style={{ background: '#333', padding: '8px', borderRadius: '4px' }}>
                                {cond.logic ? (
                                    <Select value={cond.logic} onChange={e => updateCondition('buyConditions', idx, 'logic', e.target.value)} style={{ margin: '0 auto' }}>
                                        <option value="AND">AND (그리고)</option>
                                        <option value="OR">OR (또는)</option>
                                    </Select>
                                ) : (
                                    <>
                                        <Select value={cond.lhs} onChange={e => updateCondition('buyConditions', idx, 'lhs', e.target.value)} style={{ flex: 1 }}>
                                            <optgroup label="기본 데이터 (OHLCV)">
                                                {ohlcOptions.map(opt => (
                                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                ))}
                                            </optgroup>
                                            <optgroup label="보조지표">
                                                {getIndicatorOptions().map(opt => (
                                                    <option key={opt.value} value={opt.value}>{opt.label} - {opt.value}</option>
                                                ))}
                                            </optgroup>
                                        </Select>
                                        <Select value={cond.operator} onChange={e => updateCondition('buyConditions', idx, 'operator', e.target.value)}>
                                            <option value=">">&gt;</option>
                                            <option value="<">&lt;</option>
                                            <option value=">=">&gt;=</option>
                                            <option value="<=">&lt;=</option>
                                            <option value="==">==</option>
                                            <option value="!=">!=</option>
                                            <option value="cross_up">Cross Up (상향돌파)</option>
                                            <option value="cross_down">Cross Down (하향이탈)</option>
                                        </Select>
                                        <Select value={cond.rhs} onChange={e => updateCondition('buyConditions', idx, 'rhs', e.target.value)} style={{ flex: 1 }}>
                                            <option value="" disabled hidden>비교대상 선택</option>
                                            <option value="0">0 (기준선)</option>
                                            <optgroup label="기본 데이터 (OHLCV)">
                                                {ohlcOptions.map(opt => (
                                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                ))}
                                            </optgroup>
                                            <optgroup label="보조지표">
                                                {getIndicatorOptions().map(opt => (
                                                    <option key={opt.value} value={opt.value}>{opt.label} - {opt.value}</option>
                                                ))}
                                            </optgroup>
                                            <optgroup label="직접 입력은 우측 칸을 이용하세요" />
                                        </Select>
                                        <Input placeholder="직접 숫자입력 (예: 30, * 1.5)" value={isNaN(cond.rhs) && cond.rhs !== '' && !cond.rhs.startsWith('ind_') && cond.rhs !== '0' && cond.rhs !== 'close' && cond.rhs !== 'volume' && !cond.rhs.includes('*') ? cond.rhs : (cond.rhs === '' ? '' : cond.rhs)} onChange={e => updateCondition('buyConditions', idx, 'rhs', e.target.value)} style={{ width: '120px' }} />
                                        <IconButton onClick={() => removeCondition('buyConditions', idx)}><Trash2 size={16} /></IconButton>
                                    </>
                                )}
                            </Row>
                        ))}
                    </Section>

                    <Section>
                        <SectionTitle>
                            매도 조건 (Sell Conditions)
                            <Button style={{ marginLeft: 'auto', padding: '4px 8px' }} onClick={() => addCondition('sellConditions')}><Plus size={14} /> 추가</Button>
                        </SectionTitle>
                        {strategy.sellConditions.length === 0 && <div style={{ color: '#888', fontSize: '13px' }}>조건이 없습니다.</div>}
                        {strategy.sellConditions.map((cond, idx) => (
                            <Row key={idx} style={{ background: '#333', padding: '8px', borderRadius: '4px' }}>
                                {cond.logic ? (
                                    <Select value={cond.logic} onChange={e => updateCondition('sellConditions', idx, 'logic', e.target.value)} style={{ margin: '0 auto' }}>
                                        <option value="AND">AND (그리고)</option>
                                        <option value="OR">OR (또는)</option>
                                    </Select>
                                ) : (
                                    <>
                                        <Select value={cond.lhs} onChange={e => updateCondition('sellConditions', idx, 'lhs', e.target.value)} style={{ flex: 1 }}>
                                            <optgroup label="기본 데이터 (OHLCV)">
                                                {ohlcOptions.map(opt => (
                                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                ))}
                                            </optgroup>
                                            <optgroup label="보조지표">
                                                {getIndicatorOptions().map(opt => (
                                                    <option key={opt.value} value={opt.value}>{opt.label} - {opt.value}</option>
                                                ))}
                                            </optgroup>
                                        </Select>
                                        <Select value={cond.operator} onChange={e => updateCondition('sellConditions', idx, 'operator', e.target.value)}>
                                            <option value=">">&gt;</option>
                                            <option value="<">&lt;</option>
                                            <option value=">=">&gt;=</option>
                                            <option value="<=">&lt;=</option>
                                            <option value="==">==</option>
                                            <option value="!=">!=</option>
                                            <option value="cross_up">Cross Up (상향돌파)</option>
                                            <option value="cross_down">Cross Down (하향이탈)</option>
                                        </Select>
                                        <Select value={cond.rhs} onChange={e => updateCondition('sellConditions', idx, 'rhs', e.target.value)} style={{ flex: 1 }}>
                                            <option value="" disabled hidden>비교대상 선택</option>
                                            <option value="0">0 (기준선)</option>
                                            <optgroup label="기본 데이터 (OHLCV)">
                                                {ohlcOptions.map(opt => (
                                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                ))}
                                            </optgroup>
                                            <optgroup label="보조지표">
                                                {getIndicatorOptions().map(opt => (
                                                    <option key={opt.value} value={opt.value}>{opt.label} - {opt.value}</option>
                                                ))}
                                            </optgroup>
                                            <optgroup label="직접 입력은 우측 칸을 이용하세요" />
                                        </Select>
                                        <Input placeholder="직접 숫자입력 (예: 30, * 1.5)" value={isNaN(cond.rhs) && cond.rhs !== '' && !cond.rhs.startsWith('ind_') && cond.rhs !== '0' && cond.rhs !== 'close' && cond.rhs !== 'volume' && !cond.rhs.includes('*') ? cond.rhs : (cond.rhs === '' ? '' : cond.rhs)} onChange={e => updateCondition('sellConditions', idx, 'rhs', e.target.value)} style={{ width: '120px' }} />
                                        <IconButton onClick={() => removeCondition('sellConditions', idx)}><Trash2 size={16} /></IconButton>
                                    </>
                                )}
                            </Row>
                        ))}
                    </Section>

                </Body>
                <Footer>
                    {selectedStrategyId !== 'new' && <Button onClick={handleDelete} style={{ marginRight: 'auto', background: '#e53935' }}>삭제</Button>}
                    {selectedStrategyId !== 'new' && <Button onClick={handleClone} style={{ marginRight: 'auto', background: '#4CAF50' }}>[+] 이 전략을 복사하여 새로 만들기</Button>}
                    <Button onClick={onClose}>취소</Button>
                    <Button primary onClick={handleSave}><Save size={16} /> 전략 저장</Button>
                </Footer>
            </ModalContent>
        </Overlay>
    );
}
